import { ALL_SYSTEM_MODULES, MODULES_WITH_EXPORT, SYSTEM_MODULE_LABELS, SystemModule } from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createSystemRole,
  getApiErrorMessage,
  getSystemRole,
  updateSystemRole,
} from '../../api/client';
import type { ModulePermission } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { canEditRoles, canWriteRoles } from '../../utils/permissions';

function defaultPermissions(): Omit<ModulePermission, 'id'>[] {
  return ALL_SYSTEM_MODULES.map((module) => ({
    module,
    canRead: false,
    canWrite: false,
    canEdit: false,
    canExport: false,
  }));
}

export default function RoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [modulePermissions, setModulePermissions] = useState(defaultPermissions());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = currentUser && (isNew ? canWriteRoles(currentUser) : canEditRoles(currentUser));

  useEffect(() => {
    if (isNew || !id) return;
    getSystemRole(id)
      .then((role) => {
        setName(role.name);
        setDescription(role.description ?? '');
        setIsActive(role.isActive);
        const perms = defaultPermissions().map((p) => {
          const existing = role.permissions.find((m) => m.module === p.module);
          return existing
            ? {
                module: existing.module,
                canRead: existing.canRead,
                canWrite: existing.canWrite,
                canEdit: existing.canEdit,
                canExport: existing.canExport,
              }
            : p;
        });
        setModulePermissions(perms);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    const activePerms = modulePermissions.filter(
      (p) => p.canRead || p.canWrite || p.canEdit || p.canExport,
    );
    if (activePerms.length === 0) {
      setError('Grant at least one permission on one module');
      setSaving(false);
      return;
    }
    try {
      if (isNew) {
        const created = await createSystemRole({
          name: name.trim(),
          description: description.trim() || undefined,
          isActive,
          modulePermissions: activePerms,
        });
        navigate('/roles');
      } else if (id) {
        await updateSystemRole(id, {
          name: name.trim(),
          description: description.trim() || null,
          isActive,
          modulePermissions: activePerms,
        });
        navigate('/roles');
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) {
    return (
      <div className="alert alert-error">
        You do not have permission to {isNew ? 'create' : 'edit'} roles.{' '}
        <Link to="/roles">Back to roles</Link>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading role…</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/roles" style={{ fontSize: '0.85rem' }}>
            ← Role Management
          </Link>
          <h1 style={{ marginTop: '0.35rem' }}>{isNew ? 'New role' : name || 'Edit role'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="card card-body" onSubmit={handleSave}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="role-name">Role name</label>
            <input
              id="role-name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={!canEdit}
            />
          </div>
          {!isNew && (
            <div className="form-group">
              <label htmlFor="role-active">Status</label>
              <select
                id="role-active"
                className="form-control"
                value={isActive ? 'yes' : 'no'}
                onChange={(e) => setIsActive(e.target.value === 'yes')}
                disabled={!canEdit}
              >
                <option value="yes">Active</option>
                <option value="no">Inactive</option>
              </select>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="role-desc">Description</label>
          <textarea
            id="role-desc"
            className="form-control"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem' }}>
          <legend>Module permissions</legend>
          <p style={{ margin: '0 0 1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Read = view data · Write = create · Edit = update/delete · Export = download exports
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Read</th>
                  <th>Write</th>
                  <th>Edit</th>
                  <th>Export</th>
                </tr>
              </thead>
              <tbody>
                {modulePermissions.map((p, idx) => (
                  <tr key={p.module}>
                    <td>{SYSTEM_MODULE_LABELS[p.module as SystemModule]}</td>
                    {(['canRead', 'canWrite', 'canEdit', 'canExport'] as const).map((field) => {
                      if (field === 'canExport' && !MODULES_WITH_EXPORT.includes(p.module as SystemModule)) {
                        return (
                          <td key={field} style={{ color: 'var(--text-muted)' }}>
                            —
                          </td>
                        );
                      }
                      return (
                        <td key={field}>
                          <input
                            type="checkbox"
                            checked={p[field]}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const next = [...modulePermissions];
                              next[idx] = { ...p, [field]: e.target.checked };
                              setModulePermissions(next);
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </fieldset>

        {canEdit && (
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
            {saving ? 'Saving…' : isNew ? 'Create role' : 'Save changes'}
          </button>
        )}
      </form>
    </>
  );
}
